import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { GridsterConfig, GridsterItem } from 'angular-gridster2';
import { Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

import {
    AzavistaApiService, IDeleteGlobalSettingItemRequest, IGetGlobalSettingRequest,
    ISaveGlobalSettingItemRequest, ISearchEventsRequest, ISettingItem, IUpdateGlobalSettingItemRequest
} from '@azavista/servicelib';
import { IWidgetComponentDefinition } from '@azavista/components/widgets';
import { IAzavistaAvailableWidgetsItem, IDashboardItem, IDashboardSettings, IDashboardWidgetCustomEventArgs, IDisabledWidgetActions } from '@azavista/components/dashboard';
import {
    AclService, ReportType, SettingEntityType, SettingsService, SettingType, SharedService, TRANSLATION_MAP, TranslationKey,
    UsageAnalyticsAction, UsageAnalyticsMainModule, UsageAnalyticsViewType, WidgetDefinitionDataType
} from '../shared';
import { WidgetsService } from '../shared/widgets/widgets.service';
import { WidgetsSharedService } from '../shared/widgets/widgets-shared.service';
import { CreateNewDashboardPopupComponent } from '../shared/create-new-dashboard-popup/create-new-dashboard-popup.component';
import { ICreateNewDashboardComponentData } from '../shared/create-new-dashboard-popup/interfaces';
import { UsageAnalyticsService } from '../usage-analytics.service';

@Component({
    templateUrl: 'global-dashboard.component.html',
})
export class GlobalDashboardComponent implements OnInit, OnDestroy {
    gridsterOptions: GridsterConfig;
    availableWidgets: IAzavistaAvailableWidgetsItem[] = [];
    dashboardLayout: GridsterItem[] = [];
    dashboardWidgets: IWidgetComponentDefinition[] = [];
    dashboards: ISettingItem[];
    activeDashboardId: string;
    activeDashboard: ISettingItem;
    renderDashboard = true;
    canShareDashboard = false;
    disableEditDashboard = false;
    disableAddWidget = false;
    disabledWidgetActions: IDisabledWidgetActions = { edit: false, delete: false };

    private saveDashboard$ = new Subject<IDashboardSettings>();
    private destroyed$ = new Subject<void>();

    constructor(
        private apiSvc: AzavistaApiService,
        private widgetsSharedSvc: WidgetsSharedService,
        private widgetsSvc: WidgetsService,
        private settingsSvc: SettingsService,
        private matDialog: MatDialog,
        private sharedSvc: SharedService,
        private aclSvc: AclService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private usageAnalyticsSvc: UsageAnalyticsService
    ) {
    }

    async ngOnInit(): Promise<void> {
        const shouldNavigateToLastUpdatedEvent = this.activatedRoute.snapshot.queryParamMap.get('navigateToLastUpdatedEvent') === 'true';
        if (shouldNavigateToLastUpdatedEvent) {
            const navigated = await this.navigateToLastUpdatedEvent();
            if (navigated) {
                return;
            }
        }
        this.canShareDashboard = this.aclSvc.isAdmin();
        await this.getDashboard();
        this.availableWidgets = this.getAvailableWidgets();
        this.saveDashboard$
            .pipe(
                takeUntil(this.destroyed$),
                auditTime(500)
            )
            .subscribe({
                next: ({ widgets, layout, compactType }) => this.saveDashboard({
                    widgets,
                    layout,
                    compactType
                })
            });
    }

    async navigateToLastUpdatedEvent(): Promise<boolean> {
        const req: ISearchEventsRequest = {
            limit: 1, offset: 0, sortFieldName: 'updated_at', sortDirection: 'desc'
        };
        const res = await this.apiSvc.searchEvents(req);
        if (res.events.length > 0) {
            const event = res.events[0];
            const queryParams = { eventId: event.id, type: event.type };
            this.router.navigate(['event', 'dashboard'], { queryParams });
            return true;
        }
        return false;
    }

    onWidgetCustomEvent(args: IDashboardWidgetCustomEventArgs): void {
        this.sharedSvc.processWidgetCustomEvent(args);
    }

    getAvailableWidgets(): IAzavistaAvailableWidgetsItem[] {
        const widgets: IAzavistaAvailableWidgetsItem[] = [];

        const eventsByTypeWidget = this.widgetsSharedSvc.createAvailableWidgetItem(
            TranslationKey.eventsByEventTypeWidgetTitle,
            TranslationKey.eventsByEventTypeWidgetDescription,
            TranslationKey.eventsByEventTypeWidgetExample,
            WidgetDefinitionDataType.eventsByEventType,
            ReportType.eventGroupBy,
            null
        );
        const participantsByTypeGlobal = this.widgetsSharedSvc.createAvailableWidgetItem(
            TranslationKey.participantsByEventTypeWidgetTitle,
            TranslationKey.participantsByEventTypeWidgetDescription,
            TranslationKey.participantsByEventTypeWidgetExample,
            WidgetDefinitionDataType.participantsByEventType,
            ReportType.participantGroupBy,
            null
        );
        const countdownClockGlobal = this.widgetsSharedSvc.createAvailableWidgetItem(
            TranslationKey.countdownClockWidgetTitle,
            TranslationKey.countdownClockWidgetDescription,
            TranslationKey.countdownClockWidgetExample,
            WidgetDefinitionDataType.countdownClock,
            ReportType.participantGroupBy,
            null
        );
        const participantsByStageWidget = this.widgetsSharedSvc.createAvailableWidgetItem(
            TranslationKey.participantsByStageWidgetTitle,
            TranslationKey.participantsByStageWidgetDescription,
            TranslationKey.participantsByStageWidgetExample,
            WidgetDefinitionDataType.participantsByStage,
            ReportType.participantGroupBy,
            null
        );
        const activityParticipantGroupByStatusWidget = this.widgetsSharedSvc.createAvailableWidgetItem(
            TranslationKey.activityParticipantGroupByStatusWidgetTitle,
            TranslationKey.activityParticipantGroupByStatusWidgetDescription,
            TranslationKey.activityParticipantGroupByStatusWidgetExample,
            WidgetDefinitionDataType.activityParticipantGroupByStatus,
            ReportType.activityParticipantGroupByStatus,
            null
        );
        const objectCount = this.widgetsSharedSvc.createAvailableWidgetItem(
            TranslationKey.objectCountWidgetTitle,
            TranslationKey.objectCountWidgetDescription,
            TranslationKey.objectCountWidgetExample,
            WidgetDefinitionDataType.objectCount,
            ReportType.objectCount,
            null
        );
        const eventWaitingList = this.widgetsSharedSvc.createAvailableWidgetItem(
            TranslationKey.eventWaitingListWidgetTitle,
            TranslationKey.eventWaitingListWidgetDescription,
            TranslationKey.eventWaitingListWidgetExample,
            WidgetDefinitionDataType.eventWaitingList,
            ReportType.eventWaitingList,
            null
        );
        const salesByProduct = this.widgetsSharedSvc.createAvailableWidgetItem(
            TranslationKey.salesByProductWidgetTitle,
            TranslationKey.salesByProductWidgetDescription,
            null, // TranslationKey.salesByProductWidgetExample,
            WidgetDefinitionDataType.salesByProduct,
            ReportType.salesByProduct,
            null
        );
        const salesByProductQuantity = this.widgetsSharedSvc.createAvailableWidgetItem(
            TRANSLATION_MAP.SALES_BY_PRODUCT_QUANTITY_WIDGET_TITLE,
            TRANSLATION_MAP.SALES_BY_PRODUCT_QUANTITY_WIDGET_DESCRIPTION,
            null, // TranslationKey.salesByProductWidgetExample,
            WidgetDefinitionDataType.salesByProductQuantity,
            ReportType.salesByProductQuantity,
            null
        );
        const emailCampaignDelivery = this.widgetsSharedSvc.createAvailableWidgetItem(
            TRANSLATION_MAP.EMAIL_CAMPAIGN_DELIVERY_WIDGET_TITLE,
            TRANSLATION_MAP.EMAIL_CAMPAIGN_DELIVERY_WIDGET_DESCRIPTION,
            null,
            WidgetDefinitionDataType.emailCampaignDelivery,
            ReportType.emailCampaignDelivery,
            null
        );

        widgets.push(
            eventsByTypeWidget,
            participantsByTypeGlobal,
            objectCount,
            countdownClockGlobal,
            participantsByStageWidget,
            activityParticipantGroupByStatusWidget,
            eventWaitingList,
            emailCampaignDelivery,
            salesByProduct,
            salesByProductQuantity,
        );
        return widgets;
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    dashboardChanged(dashboardSettings: IDashboardSettings): void {
        this.saveDashboard$.next(dashboardSettings);
    }

    private async saveDashboard(dashboardSettings: IDashboardSettings): Promise<void> {
        const dashboardSetting = {
            dashboard: {
                layout: dashboardSettings.layout,
                widgets: this.widgetsSvc.definitionsToWidgets(dashboardSettings.widgets),
                compactType: dashboardSettings.compactType
            }
        };
        const req: IUpdateGlobalSettingItemRequest = {
            entityType: SettingEntityType.global,
            settingType: SettingType.dashboard,
            settingItemId: this.activeDashboard.id,
            settingItem: {
                name: this.activeDashboard.name,
                shared: this.activeDashboard.shared,
                view_by_all: this.activeDashboard.view_by_all,
                team_ids: this.activeDashboard.team_ids,
                setting: dashboardSetting
            }
        };
        this.nameDashboardIfNotNamed(req.settingItem);
        this.adjustSharePropertiesForCurrentUser(req.settingItem);
        if (this.activeDashboard) {
            this.activeDashboard.setting = dashboardSetting;
        }
        if (this.canShareDashboard || !this.activeDashboard.shared) {
            await this.apiSvc.updateGlobalSettingItem(req);
        }
    }

    adjustSharePropertiesForCurrentUser(settingItem: ISettingItem): void {
        if (!this.canShareDashboard) {
            delete settingItem.shared;
            delete settingItem.view_by_all;
            delete settingItem.team_ids;
        }
    }

    private async getDashboard(): Promise<void> {
        const req: IGetGlobalSettingRequest = {
            entityType: SettingEntityType.global,
            settingType: SettingType.dashboard
        };
        const response = await this.settingsSvc.getGlobalDashboard(req);
        if (response) {
            this.dashboards = response.settings;
            this.activeDashboardId = response.active_setting_id || this.dashboards[0]?.id;
            this.setDashboard();
        }
    }

    setDashboard(): void {
        if (!this.dashboards.length) {
            this.resetDashboard();
            return;
        }
        this.renderDashboard = false;
        this.activeDashboard = this.dashboards.find(x => x.id === this.activeDashboardId) || this.dashboards[0];
        this.enableOrDisableDashboardFunctionality();
        const setting = this.activeDashboard.setting;
        if (setting.dashboard && (setting.dashboard.layout && setting.dashboard.widgets)) {
            this.dashboardLayout = setting.dashboard.layout;
            this.dashboardWidgets = this.widgetsSvc.widgetsToDefinitions(setting.dashboard.widgets, null);
        } else {
            this.resetDashboard();
        }
        if (setting.dashboard?.compactType) {
            this.gridsterOptions = { ...this.gridsterOptions, compactType: setting.dashboard.compactType };
        }
        setTimeout(() => this.renderDashboard = true); // we want to re-render the component with the new layout and widgets data
    }

    enableOrDisableDashboardFunctionality(): void {
        this.disableEditDashboard = this.activeDashboard?.shared && !this.canShareDashboard;
        this.disableAddWidget = this.activeDashboard?.shared && !this.canShareDashboard;
        this.disabledWidgetActions.edit = this.activeDashboard?.shared && !this.canShareDashboard;
        this.disabledWidgetActions.delete = this.activeDashboard?.shared && !this.canShareDashboard;
    }

    resetDashboard(): void {
        this.dashboardLayout = [];
        this.dashboardWidgets = [];
    }

    async onDashboardSelected(dashboard: IDashboardItem): Promise<void> {
        this.activeDashboard = null;
        this.activeDashboardId = dashboard?.id;
        if (this.canShareDashboard) {
            const targetSettingItem = this.dashboards.find(x => x.id === this.activeDashboardId);
            const req: IUpdateGlobalSettingItemRequest = {
                entityType: SettingEntityType.global,
                settingType: SettingType.dashboard,
                settingItemId: this.activeDashboardId,
                settingItem: targetSettingItem
            };
            req.settingItem.active = true;
            this.nameDashboardIfNotNamed(req.settingItem);
            this.adjustSharePropertiesForCurrentUser(req.settingItem);
            await this.apiSvc.updateGlobalSettingItem(req);
            await this.getDashboard();
        }
        this.setDashboard();
        this.usageAnalyticsSvc.addEvent({
            mainModule: UsageAnalyticsMainModule.generalDashboard,
            view: UsageAnalyticsViewType.dashboard,
            action: UsageAnalyticsAction.select
        });
    }

    nameDashboardIfNotNamed(settingItem: ISettingItem): void {
        if (!settingItem.name) {
            // For backwards compatibility: we used to have one and only one dashboard (not named)
            // The server does not accept null names
            settingItem.name = this.sharedSvc.translate(TranslationKey.unnamedDashboard);
        }
    }

    onEditDashboard(): void {
        this.openSettingItemDialog(this.sharedSvc.translate(TranslationKey.editDashboard));
    }

    onCreateDashboard(): void {
        this.openSettingItemDialog(this.sharedSvc.translate(TranslationKey.createDashboard), true);
    }

    openSettingItemDialog(dialogTitle: string, createNew = false): void {
        if (!this.activeDashboard && this.dashboards.length) {
            // prevents the user from open the dialog while re-rendering the dashboard when switching to another dashbaord
            return;
        }
        const activeDashboard = createNew ? null : { ...this.activeDashboard };
        const dialogData: ICreateNewDashboardComponentData = {
            dialogTitle: dialogTitle,
            settingItem: activeDashboard,
            canShareItem: this.canShareDashboard,
            dashboards: createNew ? this.dashboards : null
        };
        const dialogRef = this.matDialog.open(CreateNewDashboardPopupComponent, {
            data: dialogData,
            width: this.sharedSvc.getNewDialogCssWidth()
        });
        dialogRef.afterClosed().subscribe(async result => {
            if (!result) {
                return;
            }
            if (result.delete) {
                const req: IDeleteGlobalSettingItemRequest = {
                    entityType: SettingEntityType.global,
                    settingType: SettingType.dashboard,
                    settingItemId: result.settingItem.id
                };
                await this.apiSvc.deleteGlobalSettingItem(req);
            }
            if (result.isNew) {
                const req: ISaveGlobalSettingItemRequest = {
                    entityType: SettingEntityType.global,
                    settingType: SettingType.dashboard,
                    settingItem: result.settingItem
                };
                req.settingItem.setting ||= {};
                req.settingItem.active = true;
                await this.apiSvc.saveGlobalSettingItem(req);
                this.usageAnalyticsSvc.addEvent({
                    mainModule: UsageAnalyticsMainModule.generalDashboard,
                    view: UsageAnalyticsViewType.createDashboardPopup,
                    action: UsageAnalyticsAction.create
                });
            }
            if (!result.isNew && !result.delete) {
                const req: IUpdateGlobalSettingItemRequest = {
                    entityType: SettingEntityType.global,
                    settingType: SettingType.dashboard,
                    settingItemId: result.settingItem.id,
                    settingItem: result.settingItem
                };
                req.settingItem.active = true;
                await this.apiSvc.updateGlobalSettingItem(req);
            }
            await this.getDashboard();
        });
    }

}
